import { FC, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { CircularProgress, IconButton } from '@mui/material';
import { PersonRemove } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { getUserInfo, getUserInfoLoader } from 'redux/reducers/user/selectors';
import {
	detachEmUserRequest,
	getConnectedEmerchantsRequest,
} from 'redux/reducers/accessManagement/reducer';
import {
	selectConnectedEmerchants,
	selectConnectedEmerchantsLoading,
} from 'redux/reducers/accessManagement/selectors';
import {
	getUserMgmtConnectedEmerchantsRequest,
	detachUserMgmtEmUserRequest,
} from 'redux/reducers/userMgmtConnectedEmerchants/reducer';
import {
	selectUserMgmtConnectedEmerchants,
	selectUserMgmtConnectedEmerchantsLoading,
} from 'redux/reducers/userMgmtConnectedEmerchants/selectors';
import { TConnectedEmerchantsResponseItem } from 'redux/reducers/accessManagement/types';
import { useParams } from 'react-router-dom';
import { EasTooltip } from '../../../../ui/EasTooltip';
import { ConfirmationDialog } from '../../../../ui/ConfirmationDialog';
import { EasTableValue } from '../../../../ui/EasTable/EasTableRow/EasTableValue';
import { ETableStructureType } from '../../../../ui/EasTable/types';
import { TConnectedEmerchantsProps } from './types';
import { COLORS } from '../../../../services/constants/colors';

export const ConnectedEmerchants: FC<TConnectedEmerchantsProps> = ({
	email: emailProp,
	source = 'userMenu',
}) => {
	const dispatch = useDispatch();
	const { emId } = useParams<{ emId?: string }>();
	const userinfo = useSelector(getUserInfo);
	const userInfoLoader = useSelector(getUserInfoLoader);

	const connectedEmerchantsUserMenu = useSelector(selectConnectedEmerchants);
	const connectedEmerchantsLoadingUserMenu = useSelector(selectConnectedEmerchantsLoading);

	const connectedEmerchantsUserMgmt = useSelector(selectUserMgmtConnectedEmerchants);
	const connectedEmerchantsLoadingUserMgmt = useSelector(selectUserMgmtConnectedEmerchantsLoading);

	const connectedEmerchants =
		source === 'userManagement' ? connectedEmerchantsUserMgmt : connectedEmerchantsUserMenu;
	const connectedEmerchantsLoading =
		source === 'userManagement'
			? connectedEmerchantsLoadingUserMgmt
			: connectedEmerchantsLoadingUserMenu;

	const [openDisconnectDialog, setOpenDisconnectDialog] = useState(false);
	const [selectedEm, setSelectedEm] = useState<number | null>(null);

	const email = emailProp ?? userinfo?.email;

	const handleDisconnectFromEm = () => {
		if (!selectedEm) return;
		if (!email) return;

		const detachPayload = {
			body: { email, emId: Number(selectedEm) },
			getEmKeycloakUsers:
				emId && +emId === selectedEm ? { emId: Number(emId), pageNumber: 1, pageSize: 100 } : null,
		};

		if (source === 'userManagement') {
			dispatch(detachUserMgmtEmUserRequest(detachPayload));
		} else {
			dispatch(detachEmUserRequest(detachPayload));
		}
		setOpenDisconnectDialog(false);
	};

	useEffect(() => {
		if (!email) return;
		if (source === 'userManagement') {
			dispatch(getUserMgmtConnectedEmerchantsRequest({ email }));
		} else {
			dispatch(getConnectedEmerchantsRequest({ email }));
		}
	}, [dispatch, email, source]);

	// if (!connectedEmerchants || connectedEmerchants.length === 0) return null;

	const loader = source === 'userManagement' ? connectedEmerchantsLoading : false;

	return (
		<>
			<Box sx={{ m: 2 }}>
				<Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
					Connected Emerchants:
				</Typography>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
					{loader && (
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								height: '100%',
							}}
						>
							<CircularProgress size={20} />
						</Box>
					)}
					{!loader &&
						connectedEmerchants &&
						connectedEmerchants.length > 0 &&
						connectedEmerchants.map((em: TConnectedEmerchantsResponseItem) => (
							<Box
								key={em.emId}
								sx={{
									display: 'flex',
									justifyContent: 'space-between',
									alignItems: 'start',
									gap: 1,
								}}
							>
								<Box>
									<EasTableValue
										value={{ id: em.emId, name: em.fullName }}
										type={ETableStructureType.EM}
										size="l"
									/>
									<Typography variant="caption" color="text.secondary" sx={{ p: 0, m: 0, mt: -1 }}>
										TIN: {em.tin}
									</Typography>
								</Box>
								<EasTooltip title="Disconnect">
									<IconButton
										size="small"
										sx={{ height: '22px' }}
										onClick={() => {
											setSelectedEm(em.emId);
											setOpenDisconnectDialog(true);
										}}
										color="error"
									>
										<PersonRemove sx={{ fontSize: '1.6rem', p: 0, m: 0 }} />
									</IconButton>
								</EasTooltip>
							</Box>
						))}
					{!loader && connectedEmerchants && connectedEmerchants.length === 0 && (
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								height: '100%',
							}}
						>
							<Typography variant="body1" color={COLORS.PRIMARY} fontWeight="bold">
								No connected merchants found
							</Typography>
						</Box>
					)}
				</Box>
			</Box>
			<ConfirmationDialog
				open={openDisconnectDialog}
				setClose={() => setOpenDisconnectDialog(false)}
				handleConfirm={handleDisconnectFromEm}
				title="Disconnect from EM"
				text="Are you sure you want to disconnect your account from EM?"
			/>
		</>
	);
};
